import React, { useEffect, useState } from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Textarea } from "@replo/design-system/components/textarea/Textarea";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { projectRouter } from "@v2/trpc/routes/project";
import { Navigate } from "react-router-dom";

const ScriptManager: React.FC = () => {
  const projectId = useCurrentProjectId();
  const { project } = useCurrentProjectContext();
  const defaultScript = "<script></script>";

  const [script, setScript] = useState<string | null>(null);

  const { data: scriptData, isLoading: isScriptLoading } =
    projectRouter.getScript.useQuery(
      { projectId: projectId || "" },
      { enabled: Boolean(projectId) },
    );

  useEffect(() => {
    if (scriptData && scriptData.script !== null) {
      setScript(scriptData.script);
    }
  }, [scriptData]);

  const updateProjectScript = projectRouter.createOrUpdateScript.useMutation();

  const handleSave = async () => {
    if (!projectId || script === null) {
      return;
    }

    try {
      await updateProjectScript.mutateAsync({
        projectId,
        script,
      });
    } catch (error) {
      console.error("Failed to save script:", error);
    }
  };

  if (project && project?.hostingType !== "replo") {
    return <Navigate to={`/shops/${projectId}/settings/shop/general`} />;
  }

  return (
    <div className="flex w-[616px] flex-col gap-4 pb-4">
      <div className="border-0.5 border-border flex flex-col gap-4 rounded-xl p-5 shadow-sm h-full w-full">
        <div className="flex flex-col">
          <h3 className="text-base font-semibold leading-none tracking-tight mb-1">
            Script Manager
          </h3>
          <p className="text-muted text-sm">
            Your custom script will be injected into all your pages.
          </p>
        </div>
        <div className="flex flex-col gap-6 flex-1">
          {isScriptLoading ? (
            <Skeleton className="h-[300px] w-full" />
          ) : (
            <Textarea
              value={script ?? undefined}
              onChange={(e) => setScript(e.target.value)}
              placeholder={defaultScript}
              variant="long"
              UNSAFE_className="h-full w-full min-h-[300px] text-placeholder font-mono"
            />
          )}
        </div>
        <div className="h-px w-full bg-border" />
        <div className="flex items-center p-0 justify-end">
          <Button
            variant="primary"
            size="base"
            onClick={() => {
              void handleSave();
            }}
            disabled={
              updateProjectScript.isPending ||
              isScriptLoading ||
              script === null
            }
          >
            {updateProjectScript.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScriptManager;
